import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, MapPin } from 'lucide-react';

/**
 * Custom market select for marketing search (hero + mobile modal).
 * Avoids native <select> double-arrow chrome.
 */
const MarketingMarketSelect = ({
  value = '',
  onChange,
  options = [],
  loading = false,
  placeholder = 'Select a market',
  emptyLabel = 'No markets available',
  loadingLabel = 'Loading markets…',
  variant = 'hero', // 'hero' | 'field'
  onOpenChange,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);
  const disabled = loading || options.length === 0;

  const displayLabel = loading
    ? loadingLabel
    : options.length === 0
      ? emptyLabel
      : selected?.label || placeholder;

  const setOpenState = (next) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpenState(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenState(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const isHero = variant === 'hero';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          if (!disabled) setOpenState(!open);
        }}
        className={
          isHero
            ? 'flex w-full items-center gap-2 bg-transparent p-0 text-left disabled:opacity-60'
            : `flex w-full items-center gap-2 rounded-xl border bg-m-canvas-light py-3 pl-3.5 pr-3 text-left transition-colors disabled:opacity-60 ${
                open ? 'border-m-ink' : 'border-m-border-light'
              }`
        }
      >
        <MapPin
          className={`h-3.5 w-3.5 shrink-0 ${
            open || selected ? 'text-m-ink' : 'text-m-ink-faint'
          }`}
        />
        <span
          className={`min-w-0 flex-1 truncate text-sm font-normal ${
            selected ? 'text-m-ink' : 'text-m-ink-faint'
          }`}
        >
          {displayLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-m-ink-faint transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          className={`absolute z-50 mt-2 max-h-56 w-full overflow-auto rounded-xl border border-m-border-light bg-white py-1 shadow-[0_12px_40px_rgba(0,0,0,0.12)] ${
            isHero ? 'left-0 min-w-[14rem]' : 'left-0 right-0'
          }`}
        >
          <li role="option">
            <button
              type="button"
              onClick={() => {
                onChange?.('');
                setOpenState(false);
              }}
              className={`flex w-full px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-m-canvas-light ${
                !value ? 'font-medium text-m-ink' : 'text-m-ink-muted'
              }`}
            >
              {placeholder}
            </button>
          </li>
          {options.map((opt) => {
            const active = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpenState(false);
                  }}
                  className={`flex w-full px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-m-canvas-light ${
                    active ? 'font-medium text-m-ink' : 'text-m-ink-muted'
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default MarketingMarketSelect;
